import api from './client';
import type { Ordre, OrdreIntervention } from 'types/ordre';
import type { ListParams } from './types';

/** A pending "à prévoir" line surfaced for re-proposal at the next RDV. */
export interface PendingAPrevoir {
  ordre_id: string;
  ordre_num?: string;
  created_at?: { seconds: number; nanoseconds?: number };
  intervention: OrdreIntervention;
}

export const ordresApi = {
  list: async (params?: ListParams & { org_id?: string; status?: string; assigned_to?: string; dossier_id?: string; has_or_number?: boolean; date_start?: number; date_end?: number; date_field?: string; offset?: number; sort_by?: string; sort_dir?: string }): Promise<{ items: Ordre[]; total: number }> => {
    const resp = await api.get('/ordres', { params });
    return { items: resp.data, total: parseInt(resp.headers['x-total-count'] || '0', 10) };
  },

  get: async (id: string): Promise<Ordre> => {
    const { data } = await api.get(`/ordres/${id}`);
    return data;
  },

  dashboardStats: async (orgId: string, forceRefresh = false): Promise<{
    total: number;
    by_status: Record<string, number>;
    prepa: number;
    en_cours: number;
    termines: number;
    restitues: number;
    bloques: number;
    urgents: number;
    cached: boolean;
    stale: boolean;
    computed_at: string;
  }> => {
    const { data } = await api.post('/ordres/dashboard-stats', { org_id: orgId, force_refresh: forceRefresh });
    return data;
  },

  batch: async (ids: string[]): Promise<Ordre[]> => {
    if (ids.length === 0) return [];
    const { data } = await api.post('/ordres/batch', { ids });
    return data;
  },

  create: async (ordre: Omit<Ordre, 'id'> & { force?: boolean }): Promise<Ordre> => {
    const { data } = await api.post('/ordres', ordre);
    return data;
  },

  update: async (id: string, updates: Partial<Ordre> & { force?: boolean }): Promise<Ordre> => {
    const { data } = await api.patch(`/ordres/${id}`, updates);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/ordres/${id}`);
  },

  uploadDocument: async (ordreId: string, file: File, name?: string, documentType?: string): Promise<Ordre> => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (documentType) formData.append('document_type', documentType);
    const { data } = await api.post(`/ordres/${ordreId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  importDocumentFromGcs: async (ordreId: string, sourceGcsPath: string, name?: string, documentType?: string): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/documents/import-from-gcs`, {
      source_gcs_path: sourceGcsPath,
      name,
      document_type: documentType,
    });
    return data;
  },

  getDocumentUrl: async (ordreId: string, documentId: string): Promise<string> => {
    const { data } = await api.get(`/ordres/${ordreId}/documents/${documentId}/url`);
    return data.url;
  },

  deleteDocument: async (ordreId: string, documentId: string): Promise<Ordre> => {
    const { data } = await api.delete(`/ordres/${ordreId}/documents/${documentId}`);
    return data;
  },

  addVehicleBookingEvent: async (ordreId: string, payload: { vehicle_plate: string; vehicle_name: string; start_date: string; end_date: string }): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/vehicle-booking-event`, payload);
    return data;
  },

  markGroupReady: async (ordreId: string, group: string): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/groups/${group}/mark-ready`);
    return data;
  },

  transferToAtelier: async (ordreId: string, group: string, charges: { specialty: string; intervention_ids: string[]; hours: number }[]): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/groups/${group}/transfer-atelier`, { charges });
    return data;
  },

  sendSignRequest: async (ordreId: string, documentId: string, signerName: string, signerEmail: string, signerPhone?: string): Promise<{ success: boolean; token: string; signing_url: string }> => {
    const { data } = await api.post(`/ordres/${ordreId}/documents/${documentId}/sign-request`, {
      signer_name: signerName,
      signer_email: signerEmail,
      signer_phone: signerPhone,
      document_id: documentId,
    });
    return data;
  },

  notifyRestitutionChange: async (ordreId: string, payload: {
    old_date_seconds?: number;
    new_date_seconds: number;
    comment?: string;
    notify?: boolean;
  }): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/notify-restitution-change`, payload);
    return data;
  },

  notifyReceptionChange: async (ordreId: string, payload: {
    old_date_seconds?: number;
    new_date_seconds: number;
    comment?: string;
    notify?: boolean;
  }): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/notify-reception-change`, payload);
    return data;
  },

  generateContratLocation: async (ordreId: string): Promise<{ documents: { id: string; name: string; type: string; gcs_path: string; url: string }[] }> => {
    const { data } = await api.post(`/ordres/${ordreId}/generate-contrat-location`);
    return data;
  },

  /** Pending "à prévoir" interventions for a dossier (optionally one VIN) to re-propose at the next RDV. */
  getPendingAPrevoir: async (params: { dossier_id: string; vin?: string; exclude_ordre_id?: string }): Promise<PendingAPrevoir[]> => {
    const { data } = await api.get('/ordres/pending-a-prevoir', { params });
    return data;
  },

  /** Mark source à-prévoir lines as consumed by a newly created ordre (kept as history, removed from the pool). */
  consumeAPrevoir: async (newOrdreId: string, items: { ordre_id: string; intervention_id: string }[]): Promise<{ consumed: number }> => {
    const { data } = await api.post('/ordres/consume-a-prevoir', { new_ordre_id: newOrdreId, items });
    return data;
  },
};
